// CommandBar - Adaptive Navigation System
// Dynamically adapts based on layoutMode: cells-grid, cells-single, benchmarking, compare

import { useLocation } from 'wouter-preact'
import { useEffect, useState } from 'preact/hooks'
import { layoutMode, activeSection } from '../../state/globalSignals.js'
import { isInCompare, toggleCompare } from '../../state/filterStore.js'
import { compareCount } from '../../state/filterStore.js'
import SettingsButton from '../shared/SettingsButton.jsx'
import GlobalControls from '../shared/GlobalControls.jsx'
import DemoUsageMeter from '../shared/DemoUsageMeter.jsx'
import { UserDropdownMenu } from './UserDropdownMenu.jsx'
import HelpMenuButton from '../../tours/HelpMenuButton.jsx'
import { currentUser, subscriptionStatus, approvalStatus, hasActiveSubscription } from '../../state/userState.js'
import NavTabs from './NavTabs.jsx'
import { appViewMode } from '../../state/appViewMode.js'
import { getSlugFromProductId } from '../../utils/cellSlugMap.js'
import { IconArrowLeft } from '../shared/Icons.jsx'
import CountryFlag from 'react-country-flag'

/**
 * Check if user has data access (active subscription with approval)
 */
function canAccessData() {
  const status = subscriptionStatus.value?.toUpperCase()
  const approval = approvalStatus.value?.toUpperCase()
  const isActive = status === 'ACTIVE' || status === 'TRIALING'
  const isApproved = approval === 'APPROVED'
  return isActive && isApproved
}

/**
 * Shared right-hand header cluster (Option c).
 *
 * Centralises everything that lives in `.cmdbar__right` so the DEMO deep-dive
 * meter is defined ONCE and is therefore present on EVERY header variant —
 * grid, cell-detail, cell-selection, compare, radar, and the minimal
 * no-subscription bar — including any future variant that reuses this cluster.
 * The meter self-guards to DEMO (renders null for every other tier), so it is
 * harmless in the minimal bar. Optik unchanged: this emits the exact same
 * `.cmdbar__right` DOM (same children order) each variant had inline before.
 *
 * @param {object} props
 * @param {boolean} [props.showGlobalControls=true] - Basis/Domain explorer
 *   controls. Off for Technology Radar (curated content, not an explorer) and
 *   the minimal no-subscription bar.
 * @param {boolean} [props.showTools=true] - Settings + Help buttons. Off for
 *   the minimal no-subscription bar.
 */
function CommandBarRightCluster({ showGlobalControls = true, showTools = true }) {
  return (
    <div className="cmdbar__right">
      <DemoUsageMeter />
      {showGlobalControls && <GlobalControls />}
      {showTools && <SettingsButton />}
      {showTools && <HelpMenuButton />}
      <UserDropdownMenu />
    </div>
  )
}

/**
 * Main CommandBar Component
 * Renders different layouts based on layoutMode and subscription status
 */
export default function CommandBar() {
  const [location, setLocation] = useLocation()
  const mode = layoutMode.value
  
  // User info from global state
  const user = currentUser.value
  
  // Check if user has data access - if not, show minimal header
  const hasDataAccess = canAccessData()
  
  if (!hasDataAccess) {
    return <MinimalCommandBar location={location} setLocation={setLocation} user={user} />
  }
  
  // Render based on mode
  switch (mode) {
    case 'cells-grid':
      return <CellsGridCommandBar location={location} setLocation={setLocation} user={user} />
    case 'cells-single':
      return <SingleCellCommandBar location={location} setLocation={setLocation} user={user} />
    case 'benchmarking':
      return <BenchmarkingCommandBar location={location} setLocation={setLocation} user={user} />
    case 'compare':
      return <CompareCommandBar location={location} setLocation={setLocation} user={user} />
    case 'radar':
      return <RadarCommandBar location={location} setLocation={setLocation} user={user} />
    default:
      return <CellsGridCommandBar location={location} setLocation={setLocation} user={user} />
  }
}

/**
 * MINIMAL MODE - For users without active subscription
 * Only shows logo and user menu, no navigation tabs
 */
function MinimalCommandBar({ location, setLocation, user }) {
  return (
    <header className="cmdbar cmdbar--minimal">
      <div className="cmdbar__main">
        <div className="cmdbar__left">
          <div className="cmdbar-logo-group">
            <a className="cmdbar-logo" href="/">
              <img src="/logo/bildmarke.svg" alt="Batemo" width="40" height="40" />
              <span className="cmdbar-logo-text">BATEMO INSIGHTS</span>
            </a>
            {/* No navigation tabs for users without subscription */}
          </div>
        </div>
        
        {/* Only the user menu — no global controls/tools. The DEMO meter is
            still mounted (self-guarded to DEMO) so a demo user landing here
            keeps their always-on deep-dive budget in view. */}
        <CommandBarRightCluster showGlobalControls={false} showTools={false} />
      </div>
    </header>
  )
}

/**
 * CELLS GRID MODE
 * Mainbar: Logo + [Cells] [Benchmarking] + Tools
 * NO Subheader - CellsContextBar handles all view controls
 */
function CellsGridCommandBar({ location, setLocation, user }) {
  const isCells = location === '/' || location === '/cells' || location === '/explorer'
  const isBenchmarking = location.startsWith('/cell-selection')
  const isCompare = location.startsWith('/compare')
  const isRadar = location.startsWith('/radar')
  const count = compareCount.value

  return (
    <header className="cmdbar cmdbar--cells-grid">
      {/* Main Navigation Bar */}
      <div className="cmdbar__main">
        <div className="cmdbar__left">
          <div className="cmdbar-logo-group">
            <a className="cmdbar-logo" href="/">
              <img src="/logo/bildmarke.svg" alt="Batemo" width="40" height="40" />
              <span className="cmdbar-logo-text">BATEMO INSIGHTS</span>
            </a>

            <NavTabs
              location={location}
              setLocation={setLocation}
              dataTour="welcome-nav-tabs"
              isCellsActive={isCells}
              isBenchmarkingActive={isBenchmarking}
              isRadarActive={isRadar}
              compareTab="auto"
              isCompareActive={isCompare}
              compareCount={count}
              onCompareClick={() => {
                // Save current specific view from appViewMode
                localStorage.setItem('previousCellsView', appViewMode.value)
                setLocation('/compare')
              }}
            />
          </div>
        </div>

        <CommandBarRightCluster />
      </div>
      {/* No Subheader - CellsContextBar handles view controls */}
    </header>
  )
}

/**
 * SINGLE CELL MODE (DEPRECATED - Will use CellsGridCommandBar instead)
 * Cell detail is now part of MainAppPage with unified-context-bar
 */
function SingleCellCommandBar({ location, setLocation, user }) {
  // Just render normal cells command bar - cell detail is in MainAppPage
  return <CellsGridCommandBar location={location} setLocation={setLocation} user={user} />
}

/**
 * BENCHMARKING MODE
 * Mainbar: Logo + [Cells] [Benchmarking*] + Filter Icon + Tools
 * Subheader: [Score] [My] [Free] Plot Tabs
 */
/**
 * BENCHMARKING MODE
 * Mainbar ONLY: Logo + [Cells] [Benchmarking*] + Tools
 * NO Subheader - BenchmarkingContextBar handles plot tabs
 */
function BenchmarkingCommandBar({ location, setLocation, user }) {
  const isCells = location === '/' || location === '/cells'
  const isBenchmarking = location.startsWith('/cell-selection')
  const isCompare = location.startsWith('/compare')
  const isRadar = location.startsWith('/radar')
  const count = compareCount.value

  return (
    <header className="cmdbar cmdbar--benchmarking">
      {/* Main Bar */}
      <div className="cmdbar__main">
        <div className="cmdbar__left">
          <div className="cmdbar-logo-group">
            <a className="cmdbar-logo" href="/">
              <img src="/logo/bildmarke.svg" alt="Batemo" width="40" height="40" />
              <span className="cmdbar-logo-text">BATEMO INSIGHTS</span>
            </a>

            <NavTabs
              location={location}
              setLocation={setLocation}
              isCellsActive={isCells}
              isBenchmarkingActive={isBenchmarking}
              isRadarActive={isRadar}
              compareTab="auto"
              isCompareActive={isCompare}
              compareCount={count}
              onCompareClick={() => {
                // Save current specific view from appViewMode
                localStorage.setItem('previousCellsView', appViewMode.value)
                setLocation('/compare')
              }}
            />
          </div>
        </div>

        <CommandBarRightCluster />
      </div>
      {/* No Subheader - BenchmarkingContextBar handles plot tabs */}
    </header>
  )
}

/**
 * COMPARE MODE
 * Only mainbar, no subheader
 * Logo + [Cells] [Benchmarking] + [Compare Badge] + Tools
 */
function CompareCommandBar({ location, setLocation, user }) {
  const isCells = location === '/' || location === '/cells'
  const isBenchmarking = location.startsWith('/cell-selection')
  const isCompare = location.startsWith('/compare')
  const isRadar = location.startsWith('/radar')
  const count = compareCount.value

  return (
    <header className="cmdbar cmdbar--compare">
      <div className="cmdbar__main">
        <div className="cmdbar__left">
          <div className="cmdbar-logo-group">
            <a className="cmdbar-logo" href="/">
              <img src="/logo/bildmarke.svg" alt="Batemo" width="40" height="40" />
              <span className="cmdbar-logo-text">BATEMO INSIGHTS</span>
            </a>

            <NavTabs
              location={location}
              setLocation={setLocation}
              isCellsActive={isCells}
              isBenchmarkingActive={isBenchmarking}
              isRadarActive={isRadar}
              compareTab="always"
              isCompareActive={isCompare}
              compareCount={count}
              onCompareClick={() => setLocation('/compare')}
            />
          </div>
        </div>

        <CommandBarRightCluster />
      </div>
    </header>
  )
}

/**
 * RADAR MODE
 * Mainbar: Logo + [Cell Explorer] [Benchmarking] [Technology Radar*]
 * NO GlobalControls — Technology Radar is curated content, not an explorer
 */
function RadarCommandBar({ location, setLocation, user }) {
  const isCells = location === '/' || location === '/cells' || location === '/explorer'
  const isBenchmarking = location.startsWith('/cell-selection')
  const isRadar = location.startsWith('/radar')

  return (
    <header className="cmdbar cmdbar--radar">
      <div className="cmdbar__main">
        <div className="cmdbar__left">
          <div className="cmdbar-logo-group">
            <a className="cmdbar-logo" href="/">
              <img src="/logo/bildmarke.svg" alt="Batemo" width="40" height="40" />
              <span className="cmdbar-logo-text">BATEMO INSIGHTS</span>
            </a>

            <NavTabs
              location={location}
              setLocation={setLocation}
              isCellsActive={isCells}
              isBenchmarkingActive={isBenchmarking}
              isRadarActive={isRadar}
              showRadarBadge={false}
              compareTab={false}
            />
          </div>
        </div>

        {/* No explorer GlobalControls here, but the deep-dive meter is a
            cross-page demo concern (WS 1.2, Decision 3): a click-through into a
            cell detail from the radar consumes a deep dive, so show the budget
            everywhere the quota can be spent. */}
        <CommandBarRightCluster showGlobalControls={false} />
      </div>
    </header>
  )
}

// UserButton replaced by UserDropdownMenu - see UserDropdownMenu.jsx

// DrawerHeaderIntegrated removed - cell detail context is now in unified-context-bar within MainAppPage
